export const GA4_MEASUREMENT_ID = process.env.GATSBY_GA4_MEASUREMENT_ID;
export const GOOGLE_ADS_ID = process.env.GATSBY_GOOGLE_ADS_ID;

export const CONSENT_STORAGE_KEY = 'safestmenu-consent';
export const OPEN_CONSENT_SETTINGS_EVENT = 'safestmenu:open-consent-settings';
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

export type ConsentChoice = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function isConsentChoice(value: string | null | undefined): value is ConsentChoice {
  return value === 'granted' || value === 'denied';
}

function readConsentCookie(): ConsentChoice | null {
  const prefix = `${CONSENT_STORAGE_KEY}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  const value = entry?.slice(prefix.length);
  return isConsentChoice(value) ? value : null;
}

export function getStoredConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null;
  const cookieChoice = readConsentCookie();
  if (cookieChoice) return cookieChoice;

  // Migrate the original host-only localStorage preference into the shared cookie.
  const legacyChoice = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!isConsentChoice(legacyChoice)) return null;
  storeConsent(legacyChoice);
  window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  return legacyChoice;
}

export function storeConsent(choice: ConsentChoice): void {
  const sharedDomain =
    window.location.hostname === 'safestmenu.com' ||
    window.location.hostname.endsWith('.safestmenu.com');
  const attributes = [
    `${CONSENT_STORAGE_KEY}=${choice}`,
    'Path=/',
    `Max-Age=${String(CONSENT_MAX_AGE_SECONDS)}`,
    'SameSite=Lax',
  ];
  if (sharedDomain) attributes.push('Domain=.safestmenu.com');
  if (window.location.protocol === 'https:') attributes.push('Secure');
  document.cookie = attributes.join('; ');
}

export function updateConsent(choice: ConsentChoice): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: choice,
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: 'denied',
  });
}

export function trackingConfigured(): boolean {
  return Boolean(GA4_MEASUREMENT_ID || GOOGLE_ADS_ID);
}

export function trackPageview(pathname: string): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path: pathname,
    page_title: document.title,
  });
}
